import clsx from "clsx";
import { FC } from "react";

import { downloadJsonFile } from "../../../../../utils/file";
import { updateSection, useSectionSettingActions } from "../../../../../utils/section";

import { AdminSectionListItem } from "./AdminSectionListItem";

import {
  serverCloneSection,
  serverExportSection,
  serverUnlinkSection,
} from "api/api-server-actions";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { showUnlinkConfirmationModal } from "components/modals/UnlinkSectionModal";
import { t } from "i18n/i18n";
import { store, storeApi, useAppDispatch } from "store/index";
import {
  sectionSelectors,
  selectAdminLinkedSectionIdsByTemplateId,
  selectAdminSectionIdsByTermAndCourse,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";
import { ComponentEventProperties } from "utils/amplitude";
import { watchForClonedSection } from "utils/hooks/use-section-clone-observer";
import { isTemplateSection } from "utils/publishing";
import { isCCMEnabled } from "utils/waffle-flags";

type AdminSectionListPanelProps = {
  title: string;
  termId: string;
  courseId: string;
  eventPrefix?: string;
  eventProperties?: ComponentEventProperties["eventProperties"];
  canEdit: boolean;
  isDisabled?: boolean;
};

const handleClone = async (
  sectionId: string,
  courseId?: string,
  termId?: string,
  asTemplate?: boolean,
) => {
  try {
    await serverCloneSection(sectionId, courseId, termId, asTemplate);
    watchForClonedSection(sectionId);

    toastSuccessMessage(t("success.toasts.clone_section_started_success"));
  } catch (error) {
    toastLocalizedOperationCatcher("copy_section_failure")(error);
  }
};

const handleToggleArchive = (sectionId: string) => {
  const section = sectionSelectors.selectById(store.getState(), sectionId);
  if (!section) {
    return;
  }
  storeApi.sections.partial_update({
    id: sectionId,
    archived_at: section.archived_at ? null : new Date().toISOString(),
  });
};

type AdminSectionListGroupProps = {
  sectionId: string;
  termId: string;
  courseId: string;
  canEdit: boolean;
  eventPrefix?: string;
  eventProperties?: ComponentEventProperties["eventProperties"];
};

const AdminSectionListGroup: FC<AdminSectionListGroupProps> = ({
  sectionId,
  termId,
  courseId,
  canEdit,
  eventPrefix,
  eventProperties,
}) => {
  const ccmEnabled = isCCMEnabled();
  const section = useObjectSelector(
    (state) => sectionSelectors.selectById(state, sectionId),
    ["kind", "title"],
  );
  const linkedSectionIds = useListSelector((state) =>
    selectAdminLinkedSectionIdsByTemplateId(state, termId, courseId, sectionId, ccmEnabled),
  );
  const { handleSectionDelete } = useSectionSettingActions();
  const dispatch = useAppDispatch();

  if (!section) {
    return null;
  }

  const buildOptionsMenuActions = (targetSectionId: string, enableUnlink: boolean) => ({
    onSettings: () => {
      dispatch(viewStateActions.setSectionSettingsForSectionId(targetSectionId));
    },
    onDelete: () => {
      const targetSection = sectionSelectors.selectById(store.getState(), targetSectionId);
      if (!targetSection) {
        return;
      }
      handleSectionDelete(targetSection, false);
    },
    onDuplicate: () => handleClone(targetSectionId),
    onDuplicateAsTemplate: () => handleClone(targetSectionId, undefined, undefined, true),
    onCopyTo: handleClone,
    onMoveTo: (nextCourseId: string, nextTermId: string) => {
      updateSection({
        id: targetSectionId,
        course_id: nextCourseId,
        term_id: nextTermId,
      });
    },
    onExport: async () => {
      const targetSection = sectionSelectors.selectById(store.getState(), targetSectionId);
      if (!targetSection) {
        return;
      }
      const sectionJson = await serverExportSection(targetSectionId);
      downloadJsonFile(sectionJson, `${targetSection.title}.json`);
    },
    onArchive: () => handleToggleArchive(targetSectionId),
    onUnarchive: () => handleToggleArchive(targetSectionId),
    onUnlinkSection: enableUnlink
      ? async () => {
          const confirmed = await showUnlinkConfirmationModal({
            sectionId: targetSectionId,
            templateTitle: section.title,
          });
          if (confirmed) {
            await serverUnlinkSection(targetSectionId);
            toastSuccessMessage(t("sidebar_options_menu.unlink_success"));
          }
        }
      : undefined,
  });

  return (
    <>
      <AdminSectionListItem
        canEdit={canEdit}
        eventPrefix={eventPrefix}
        eventProperties={eventProperties}
        linkedSectionsCount={linkedSectionIds.length}
        optionsMenuActions={buildOptionsMenuActions(sectionId, false)}
        sectionId={sectionId}
      />

      {linkedSectionIds.map((linkedSectionId) => (
        <AdminSectionListItem
          canEdit={canEdit}
          className="ml-8"
          eventPrefix={eventPrefix}
          eventProperties={eventProperties}
          key={linkedSectionId}
          optionsMenuActions={buildOptionsMenuActions(linkedSectionId, true)}
          sectionId={linkedSectionId}
        />
      ))}

      {ccmEnabled && isTemplateSection(section) && linkedSectionIds.length === 0 && (
        <li className="body-s ml-8 flex min-h-[45px] items-center gap-2 border-t border-black-tint-90 px-3 text-black-tint-55">
          <span>{t("section_list_panel.no_linked_sections")}</span>
        </li>
      )}
    </>
  );
};

export const AdminSectionListPanel: FC<AdminSectionListPanelProps> = ({
  title,
  termId,
  courseId,
  eventPrefix,
  eventProperties,
  canEdit,
  isDisabled = false,
}) => {
  const ccmEnabled = isCCMEnabled();
  const sectionIds = useListSelector((state) =>
    selectAdminSectionIdsByTermAndCourse(state, termId, courseId, ccmEnabled),
  );

  return (
    <div className="mb-6 border-b border-black-tint-90">
      <HoverableToolbar reference="section">
        <h3 className={clsx("h3 my-2", isDisabled ? "text-black-tint-55" : "text-black-tint-20")}>
          {title}
        </h3>
      </HoverableToolbar>

      <ul>
        {sectionIds.map((sectionId) => (
          <AdminSectionListGroup
            canEdit={canEdit}
            courseId={courseId}
            eventPrefix={eventPrefix}
            eventProperties={eventProperties}
            key={sectionId}
            sectionId={sectionId}
            termId={termId}
          />
        ))}
      </ul>

      {!canEdit && sectionIds.length === 0 && (
        <div className="body-s ml-2 w-full py-2 text-black-tint-40">
          {t("section_list_panel.no_sections")}
        </div>
      )}
    </div>
  );
};
